/**
 * 团队工作台接口（扩展③）。
 * 契约：
 *   GET    /teams/:id/files                       → {files}
 *   POST   /teams/:id/files {name,contentBase64}  → {file}（201；扩展名白名单 + 解码后 ≤10MB）
 *   GET    /teams/:id/files/:fileId/download      → 原始二进制（Content-Disposition 附件）
 *   DELETE /teams/:id/files/:fileId               → {deleted:true}（仅上传者或队长）
 *   GET    /teams/:id/decisions                   → {decisions}（append-only，倒序）
 *   POST   /teams/:id/decisions {title,detail?}   → {decision}（201）
 *   GET    /projects/:id/progress                 → {phases[],overall}（权重 15/35/35/15）
 *   GET    /teams/:id/health                      → TeamHealthDto（可视化③ AI 项目体检）
 */
import { api } from './client.js';
import type { ProjectProgressDto, TeamDecisionDto, TeamFileDto, TeamHealthDto } from './types.js';

export interface UploadFileInput {
  name: string;
  contentBase64: string;
}

const enc = encodeURIComponent;

export const workbenchApi = {
  listFiles: (teamId: string): Promise<{ files: TeamFileDto[] }> =>
    api.get<{ files: TeamFileDto[] }>(`/teams/${enc(teamId)}/files`),

  upload: (teamId: string, input: UploadFileInput): Promise<{ file: TeamFileDto }> =>
    api.post<{ file: TeamFileDto }>(`/teams/${enc(teamId)}/files`, input),

  /** 下载地址：交给浏览器 <a download> 走带 Authorization 的 fetch → Blob → objectURL */
  downloadPath: (teamId: string, fileId: string): string => `/teams/${enc(teamId)}/files/${enc(fileId)}/download`,

  remove: (teamId: string, fileId: string): Promise<{ deleted: boolean }> =>
    api.delete<{ deleted: boolean }>(`/teams/${enc(teamId)}/files/${enc(fileId)}`),

  listDecisions: (teamId: string): Promise<{ decisions: TeamDecisionDto[] }> =>
    api.get<{ decisions: TeamDecisionDto[] }>(`/teams/${enc(teamId)}/decisions`),

  addDecision: (teamId: string, input: { title: string; detail?: string }): Promise<{ decision: TeamDecisionDto }> =>
    api.post<{ decision: TeamDecisionDto }>(`/teams/${enc(teamId)}/decisions`, input),

  progress: (projectId: string): Promise<ProjectProgressDto> =>
    api.get<ProjectProgressDto>(`/projects/${enc(projectId)}/progress`),

  /** AI 项目体检（可视化③）：facts/risks 代码算死，LLM 仅润色且数字锁死 */
  health: (teamId: string): Promise<TeamHealthDto> => api.get<TeamHealthDto>(`/teams/${enc(teamId)}/health`),
};

/** base64 辅助：File/Blob → dataURL 剥离前缀，仅保留 base64 载荷。 */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('读取文件失败'));
    reader.onload = () => {
      const url = String(reader.result ?? '');
      const idx = url.indexOf(',');
      resolve(idx >= 0 ? url.slice(idx + 1) : url);
    };
    reader.readAsDataURL(file);
  });
}

/** 字节数 → 人类可读大小。 */
export function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
